import { MapPin, Eye, Users, TrendingUp, ShieldCheck } from "lucide-react";

const VALORES = [
    { icon: Eye, t: "Transparencia", d: "Información clara y a tiempo, siempre." },
    { icon: Users, t: "Cercanía", d: "Trato directo, sin intermediarios." },
    { icon: TrendingUp, t: "Visión", d: "Contabilidad orientada al crecimiento." },
    { icon: ShieldCheck, t: "Compromiso", d: "Presentes cuando más se necesita." },
];

const TAGS = [
    "Rocha, Uruguay",
    "Asesoramiento fiscal",
    "Proyectos de inversión",
    "Sector agropecuario",
];

const ESTELA_PHOTO =
    "https://customer-assets.emergentagent.com/job_nervous-stonebraker-7/artifacts/kbypwvjg_DSC_9297.jpg";

export default function Solution() {
    return (
        <section
            id="nosotros"
            data-testid="solution-section"
            className="relative bg-[var(--uma-cream)] py-24 md:py-32 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="text-center max-w-3xl mx-auto reveal mb-16 md:mb-20">
                    <div className="uma-eyebrow">Quiénes somos</div>
                    <h2 className="font-serif-display text-[36px] md:text-[56px] lg:text-[64px] leading-[1.02] mt-6 text-[var(--uma-forest)]">
                        Detrás del estudio,
                        <br />
                        <em className="italic font-light text-[var(--uma-gold)]">
                            una mirada estratégica.
                        </em>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Foto + Estela card */}
                    <div className="lg:col-span-5 reveal">
                        <div className="bg-[var(--uma-cream-white)] rounded-2xl overflow-hidden border border-[var(--uma-forest)]/8 shadow-[0_18px_50px_-20px_rgba(10,38,20,0.18)]">
                            <div className="aspect-[4/5] relative overflow-hidden">
                                <img
                                    src={ESTELA_PHOTO}
                                    alt="Estela Altez Palarin — Contadora Pública"
                                    className="w-full h-full object-cover object-center"
                                    loading="lazy"
                                    data-testid="estela-photo"
                                />
                                <span className="absolute top-4 left-4 w-8 h-8 border-l border-t border-[var(--uma-gold)]/80 pointer-events-none" />
                                <span className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-[var(--uma-gold)]/80 pointer-events-none" />
                            </div>
                            <div className="p-6 md:p-7">
                                <div className="font-serif-display text-[26px] md:text-[28px] text-[var(--uma-forest)] leading-tight">
                                    Estela Altez Palarin
                                </div>
                                <div className="mt-1 text-[13px] text-[var(--uma-text-soft)]">
                                    Contadora Pública · CRA Uruguay
                                </div>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {TAGS.map((t, i) => (
                                        <span
                                            key={i}
                                            className="text-[11px] uppercase tracking-[0.16em] bg-[var(--uma-cream-soft)] border border-[var(--uma-forest)]/8 text-[var(--uma-forest)] px-3 py-1.5 rounded-full"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-6 pt-5 border-t border-[var(--uma-forest)]/8 flex items-center gap-2 text-[12px] text-[var(--uma-text-soft)]">
                                    <MapPin className="h-3.5 w-3.5 text-[var(--uma-gold)]" />
                                    Rocha y Castillos, Uruguay
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Story + values */}
                    <div className="lg:col-span-7 space-y-10">
                        <div className="reveal">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-4">
                                Nuestra historia
                            </div>
                            <p className="text-[16px] md:text-[17px] leading-relaxed text-[var(--uma-text)] font-light">
                                El Estudio Contable Altez Palarin nació en Rocha
                                con una convicción clara:{" "}
                                <span className="font-medium text-[var(--uma-forest)]">
                                    las empresas del interior uruguayo merecen
                                    un asesoramiento contable que las acompañe
                                    de verdad
                                </span>
                                , no que aparezca solo cuando hay que entregar
                                un formulario.
                            </p>
                            <p className="mt-4 text-[16px] md:text-[17px] leading-relaxed text-[var(--uma-text-soft)] font-light">
                                Soy Estela Altez Palarin, Contadora Pública
                                egresada con vocación por el asesoramiento
                                integral.{" "}
                                <span className="text-[var(--uma-forest)] font-medium">
                                    Criada en una familia con arraigo en el
                                    mundo agropecuario
                                </span>
                                , conozco el campo no solo desde los libros
                                sino desde adentro — lo que me da una visión
                                única para entender las necesidades reales de
                                mis clientes, tanto en sus números como en su
                                día a día. Desde el inicio, mi enfoque estuvo
                                puesto en entender el negocio de cada cliente
                                — sus decisiones, sus proyectos — para poder
                                anticiparme, no solo registrar.
                            </p>
                        </div>

                        <div className="reveal">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-4">
                                Cómo trabajamos
                            </div>
                            <p className="text-[16px] md:text-[17px] leading-relaxed text-[var(--uma-text)] font-light">
                                Creemos que{" "}
                                <span className="font-medium text-[var(--uma-forest)]">
                                    un buen contador no te salva del problema:
                                    te ayuda a que no llegue.
                                </span>{" "}
                                Por eso nuestra forma de trabajar es activa y
                                continua: monitoreamos, alertamos y asesoramos
                                antes de que los vencimientos, las inspecciones
                                o las decisiones financieras te agarren sin
                                información.
                            </p>
                        </div>

                        <div className="reveal">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-5">
                                Nuestros valores
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {VALORES.map((v, i) => {
                                    const Icon = v.icon;
                                    return (
                                        <div
                                            key={i}
                                            data-testid={`solution-pillar-${i}`}
                                            className="group flex items-start gap-4 bg-[var(--uma-cream-white)] border border-[var(--uma-forest)]/8 rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(10,38,20,0.12)]"
                                        >
                                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--uma-cream-soft)] text-[var(--uma-forest)] flex items-center justify-center group-hover:bg-[var(--uma-forest)] group-hover:text-[var(--uma-gold)] transition-colors">
                                                <Icon className="h-4 w-4" strokeWidth={1.5} />
                                            </div>
                                            <div>
                                                <div className="font-serif-display text-[19px] text-[var(--uma-forest)] leading-tight">
                                                    {v.t}
                                                </div>
                                                <p className="mt-1 text-[13px] text-[var(--uma-text-soft)] font-light leading-relaxed">
                                                    {v.d}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
